import { AllReasonsValue, FinalResponse, ForecastQueries, Range, ReasonsValue, TableDataItem } from './useForecastTool';
import { ForecastParamCode, TreatmentScheduleForecast } from 'pleinchamp-api-client';
import { PlcColor } from '@styles/colors';
import { plcDayjs } from '@utils/date';
import { Dayjs } from 'dayjs';

type CurrentForecast = {
  date: Dayjs;
  windScore: number;
  precipitationProbabilityScore: number;
  precipitationAmountScore: number;
  precipitationFinalScore: number;
  tempDownInBound: number;
  tempDownOutBound: number;
  tempUpInBound: number;
  tempUpOutBound: number;
  tempScore: number;
  humidityDownBound: number;
  humidityUpBound: number;
  humidityScore: number;
};

interface BuildTableParams {
  treatmentScheduleForecasts?: TreatmentScheduleForecast[];
  from: Dayjs;
  forecastQueries: ForecastQueries;
}

enum BackgroundColorValue {
  darkorange = PlcColor.YELLOW_DARK_SUN,
  middleorange = PlcColor.NUDE_WHEAT,
  lightorange = PlcColor.YELLOW_SUN,
  lightgreen = PlcColor.GREEN_LEAF,
  darkgreen = PlcColor.GREEN_DARK_LEAF,
}
export type BackgroundColorLabel = keyof typeof BackgroundColorValue;
export type BackgroundColorForecast = { value: BackgroundColorValue; label: BackgroundColorLabel };

const WEEK = [0, 1, 2, 3, 4, 5, 6];

function scoring(value: number, max: number, min: number): number {
  if (value < min) {
    return 100;
  }
  if (value > max) {
    return 0;
  }
  return 100 - ((value - min) / (max - min)) * 100;
}

function calcTemp(
  value: number,
  {
    tempDownInBound,
    tempDownOutBound,
    tempUpOutBound,
    tempUpInBound,
  }: {
    tempDownInBound: number;
    tempDownOutBound: number;
    tempUpOutBound: number;
    tempUpInBound: number;
  }
): number {
  if (value >= tempDownInBound && value <= tempUpInBound) {
    return 100;
  }
  if (value < tempDownOutBound || value > tempUpOutBound) {
    return 0;
  }
  if (value < tempDownInBound) {
    return ((value - tempDownOutBound) / (tempDownInBound - tempDownOutBound)) * 100;
  }
  return 100 - ((value - tempUpInBound) / (tempUpOutBound - tempUpInBound)) * 100;
}

function calcHumidity(value: number, relativeHumidityRange: Range, hub: number, hdb: number): number {
  if (value >= relativeHumidityRange.min && value <= relativeHumidityRange.max) {
    return 100;
  }
  if (value < hdb || value > hub) {
    return 0;
  }
  if (value < relativeHumidityRange.min) {
    return ((value - hdb) / (relativeHumidityRange.min - hdb)) * 100;
  }
  return 100 - ((value - relativeHumidityRange.max) / (value - relativeHumidityRange.max)) * 100;
}

function setReasons(score: number, reasons: Array<ReasonsValue>, value: ReasonsValue): Array<ReasonsValue> {
  if (score < 50) {
    switch (value) {
      case 'W':
      case 'T':
      case 'H':
        return [...reasons, value];
      case 'R': {
        if (reasons.includes('W')) {
          const withoutWindSpeed = reasons.filter((r) => r !== 'W');
          return [...withoutWindSpeed, 'WR'];
        }
        return [...reasons, value];
      }
      default:
        return reasons;
    }
  }
  return reasons;
}

function calcFinalResult(
  windScore: number,
  precipitationFinalScore: number,
  tempScore: number,
  humidityScore: number
): number {
  return Math.min(windScore, precipitationFinalScore, tempScore, humidityScore);
}

function calcReasons(currentForecast: CurrentForecast): Array<ReasonsValue> {
  return (Object.keys(currentForecast) as Array<keyof CurrentForecast>).reduce((acc: Array<ReasonsValue>, cur) => {
    switch (cur) {
      case 'windScore':
        return setReasons(currentForecast[cur], acc, 'W');
      case 'precipitationFinalScore':
        return setReasons(currentForecast[cur], acc, 'R');
      case 'humidityScore':
        return setReasons(currentForecast[cur], acc, 'H');
      case 'tempScore':
        return setReasons(currentForecast[cur], acc, 'T');
      default:
        return acc;
    }
  }, []);
}

function getFinalReason(reasons: Array<ReasonsValue>): AllReasonsValue {
  if (reasons.length === 0) {
    return '';
  }
  if (reasons.length > 1) {
    return 'M';
  }
  return reasons[0];
}

function getColor(score: number): BackgroundColorForecast {
  if (score < 30) {
    return { label: 'darkorange', value: BackgroundColorValue.darkorange };
  }
  if (score < 50) {
    return { label: 'middleorange', value: BackgroundColorValue.middleorange };
  }
  if (score < 70) {
    return { label: 'lightorange', value: BackgroundColorValue.lightorange };
  }
  if (score < 90) {
    return { label: 'lightgreen', value: BackgroundColorValue.lightgreen };
  }
  return { label: 'darkgreen', value: BackgroundColorValue.darkgreen };
}

function calcForecastScoreItem(previsionScore: CurrentForecast) {
  const { windScore, precipitationFinalScore, tempScore, humidityScore, date: datePrevisionScore } = previsionScore;
  const finalScore = calcFinalResult(windScore, precipitationFinalScore, tempScore, humidityScore);

  return {
    color: getColor(finalScore),
    date: datePrevisionScore,
    reason: getFinalReason(calcReasons(previsionScore)),
    score: finalScore,
  };
}

function calcScores(timestepItem: TreatmentScheduleForecast, forecastQueries: ForecastQueries) {
  const {
    windSpeedRange,
    precipitationProbabilityRange,
    precipitationAmountRange,
    temperatureRange,
    temperatureSecurityBounds,
    relativeHumidityRange,
    humiditySecurityBound,
  } = forecastQueries;

  const initState: CurrentForecast = {
    date: plcDayjs(timestepItem.date),
    humidityDownBound: relativeHumidityRange.max - humiditySecurityBound,
    humidityScore: 99,
    humidityUpBound: relativeHumidityRange.min + humiditySecurityBound,
    precipitationAmountScore: 99,
    precipitationFinalScore: 99,
    precipitationProbabilityScore: 99,
    tempDownInBound: temperatureRange.min + temperatureSecurityBounds.in,
    tempDownOutBound: temperatureRange.min - temperatureSecurityBounds.out,
    tempScore: 99,
    tempUpInBound: temperatureRange.max - temperatureSecurityBounds.in,
    tempUpOutBound: temperatureRange.max + temperatureSecurityBounds.out,
    windScore: 99,
  };

  return (Object.keys(timestepItem) as (keyof TreatmentScheduleForecast)[]).reduce((acc, cur): CurrentForecast => {
    const value = timestepItem[cur] as number;
    switch (cur) {
      case ForecastParamCode.windSpeedAt2m: {
        const windScore = scoring(value, windSpeedRange.max, windSpeedRange.min);

        return {
          ...acc,
          windScore,
        };
      }
      case ForecastParamCode.precipitationProbability: {
        const precipitationProbabilityInPercentScore = scoring(
          value,
          precipitationProbabilityRange.max,
          precipitationProbabilityRange.min
        );
        const calcPrecipitationFinalScore: number =
          acc.precipitationAmountScore && !acc.precipitationFinalScore
            ? (acc.precipitationAmountScore + precipitationProbabilityInPercentScore) / 2
            : 0;

        return {
          ...acc,
          precipitationFinalScore: calcPrecipitationFinalScore,
          precipitationProbabilityScore: precipitationProbabilityInPercentScore,
        };
      }

      case ForecastParamCode.precipitationAmount: {
        const precipitationAmountInMillimeterScore = scoring(
          value,
          precipitationAmountRange.max,
          precipitationAmountRange.min
        );

        const calcPrecipitationFinalScore: number =
          acc.precipitationProbabilityScore && !acc.precipitationFinalScore
            ? (acc.precipitationProbabilityScore + precipitationAmountInMillimeterScore) / 2
            : 0;

        return {
          ...acc,
          precipitationAmountScore: precipitationAmountInMillimeterScore,
          precipitationFinalScore: calcPrecipitationFinalScore,
        };
      }

      case ForecastParamCode.airTemperature:
        return {
          ...acc,
          tempScore: calcTemp(value, {
            tempDownInBound: acc.tempDownInBound,
            tempDownOutBound: acc.tempDownOutBound,
            tempUpInBound: acc.tempUpInBound,
            tempUpOutBound: acc.tempUpOutBound,
          }),
        };

      case ForecastParamCode.relativeHumidity:
        return {
          ...acc,
          humidityScore: calcHumidity(
            acc.humidityUpBound && acc.humidityUpBound > 100 ? 100 : value,
            relativeHumidityRange,
            acc.humidityUpBound,
            acc.humidityDownBound
          ),
        };

      default:
        return acc;
    }
  }, initState);
}

function calcForecastScore(
  treatmentScheduleForecasts: TreatmentScheduleForecast[] = [],
  forecastQueries: ForecastQueries
): FinalResponse[] {
  return treatmentScheduleForecasts
    .map((treatmentScheduleForecast) => calcScores(treatmentScheduleForecast, forecastQueries))
    .map(calcForecastScoreItem);
}

function buildHourTableCells(acc: Partial<TableDataItem>, datedScore: FinalResponse) {
  const instDate = plcDayjs(datedScore.date);
  const hour = instDate.get('h').toString();
  const accProperties = Object.keys(acc);
  const isHourInDayExist = accProperties.includes(hour);

  if (!isHourInDayExist) {
    return {
      ...acc,
      [hour]: datedScore,
    };
  }
  // Hour was found do nothing
  return acc;
}

function buildTableData({ treatmentScheduleForecasts = [], from, forecastQueries }: BuildTableParams) {
  return WEEK.map((day) => {
    const nextDay = from.add(day, 'day');
    const rowDateLabel = `day-${nextDay.get('date')}`;

    const filteredTreatmentScheduleForecasts = treatmentScheduleForecasts
      ? treatmentScheduleForecasts
          // Keep all timestep in the same date
          .filter((treatmentScheduleForecast) => plcDayjs(treatmentScheduleForecast.date).isSame(nextDay, 'date'))
          .map((e) => ({ ...e, date: new Date(e.date) }))
      : [];

    const forecastScores = calcForecastScore(filteredTreatmentScheduleForecasts, forecastQueries);

    return forecastScores.reduce(buildHourTableCells, { date: nextDay, label: rowDateLabel });
  }) as TableDataItem[];
}

export { buildTableData, getColor, BackgroundColorValue };
